import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { getMe, loginWithCredentials, logoutSession } from "../lib/api.js";

/**
 * Auth context — čte `/api/auth/me`, umí přihlásit/odhlásit.
 *
 * - `AUTH_ENABLED=false` na backendu → fake admin user, login není potřeba
 * - `AUTH_ENABLED=true` + bez session → `user=null` → AuthGate ukáže LoginPage
 */

interface AuthUser {
  id: string;
  role: string;
  name?: string;
}

interface AuthCtx {
  loading: boolean;
  authEnabled: boolean;
  user: AuthUser | null;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [loading, setLoading] = useState(true);
  const [authEnabled, setAuthEnabled] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);

  async function refresh(): Promise<void> {
    try {
      const me = await getMe();
      setAuthEnabled(me.authEnabled);
      setUser(me.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function login(email: string, password: string): Promise<void> {
    const res = await loginWithCredentials(email, password);
    const next: AuthUser = { id: res.user.id, role: res.user.role };
    if (res.user.name) next.name = res.user.name;
    setUser(next);
    setAuthEnabled(true);
  }

  async function logout(): Promise<void> {
    await logoutSession();
    setUser(null);
  }

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <Ctx.Provider value={{ loading, authEnabled, user, refresh, login, logout }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
